/** The full key list, and the switch that turns the layer off (UX §9.4, §12).
 *
 * WCAG 2.1.4 asks a single-character shortcut layer for a turn-off, a remap, or focus
 * scoping. This screen provides two of the three: the scope rule (a key fires only while
 * focus is on the body or the stage) and this switch, persisted per device. The switch is not
 * a nicety; it is the conformance mechanism.
 */
import { useTranslation } from "react-i18next";

import { Button, Sheet } from "../ui";
import { Keycap } from "./Keycap";
import { SHORTCUTS } from "./shortcuts";

const GROUPS = [
  { id: "open", title: "live.keys.groupOpen" },
  { id: "closed", title: "live.keys.groupClosed" },
  { id: "queue", title: "live.keys.groupQueue" },
  { id: "always", title: "live.keys.groupAlways" },
] as const;

type Props = { enabled: boolean; onToggle: (on: boolean) => void; onClose: () => void };

export function ShortcutSheet({ enabled, onToggle, onClose }: Props) {
  const { t } = useTranslation();
  return (
    <Sheet title={t("live.keys.title")} onClose={onClose}>
      <div className="flex flex-col gap-5">
        {GROUPS.map((g) => (
          <section key={g.id} className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">{t(g.title)}</h3>
            <ul className="flex flex-col gap-1">
              {SHORTCUTS.filter((s) => s.group === g.id).map((s) => (
                <li key={s.label} className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0">{t(s.label)}</span>
                  <span className="shrink-0"><Keycap keys={s.keys} /></span>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <p className="text-sm text-[color:var(--ink-2)]">{t("live.keys.deviceOnly")}</p>
        <label className="flex items-center justify-between gap-3 rounded-[var(--radius-control)] bg-[color:var(--surface-2)] p-3 text-sm">
          <span>{t("live.keys.on")}</span>
          <input type="checkbox" className="h-6 w-6" checked={enabled} onChange={(e) => onToggle(e.target.checked)} />
        </label>
        <Button block variant="secondary" onClick={onClose}>{t("dialog.close")}</Button>
      </div>
    </Sheet>
  );
}
